<template>
  <div class="license-plate-input-wrapper">
    <BaseElInput
      :value="prefix"
      type="text"
      :placeholder="prefixPlaceholder"
      :maxlength="prefixMaxLength"
      :disabled="disabled"
      :style="{ width: prefixWidth }"
      @input="handlePrefixInput"
      @blur="handleBlur"
    />
    <span class="dash-separator">-</span>
    <BaseElInput
      :value="suffix"
      type="text"
      :placeholder="suffixPlaceholder"
      :maxlength="suffixMaxLength"
      :disabled="disabled"
      :style="{ width: suffixWidth }"
      @input="handleSuffixInput"
      @blur="handleBlur"
    />
  </div>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'

/**
 * 車牌輸入組件
 * 提供兩個輸入框，中間固定顯示 "-" 符號
 *
 * @example
 * <LicensePlateInput
 *   :value="formData.plate"
 *   @update:value="formData.plate = $event"
 * />
 */
export default defineComponent({
  name: 'LicensePlateInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prefixPlaceholder: {
      type: String,
      default: 'ABC',
    },
    suffixPlaceholder: {
      type: String,
      default: '1234',
    },
    prefixMaxLength: {
      type: Number,
      default: 10,
    },
    suffixMaxLength: {
      type: Number,
      default: 10,
    },
    prefixWidth: {
      type: String,
      default: '120px',
    },
    suffixWidth: {
      type: String,
      default: '120px',
    },
  },
  emits: ['update:value', 'blur'],
  setup (props, { emit }) {
    const prefix = ref('')
    const suffix = ref('')

    // 將完整車牌分割為前綴和後綴
    const splitPlate = (plate) => {
      if (!plate) {
        return ['', '']
      }
      const parts = plate.split('-')
      return [parts[0] || '', parts[1] || '']
    }

    // 初始化時分割 value
    const initializeValue = () => {
      const [p, s] = splitPlate(props.value)
      prefix.value = p
      suffix.value = s
    }

    // 監聽 value 變化
    watch(() => props.value, (newValue) => {
      const [p, s] = splitPlate(newValue)
      if (prefix.value !== p || suffix.value !== s) {
        prefix.value = p
        suffix.value = s
      }
    }, { immediate: true })

    // 處理前綴輸入
    const handlePrefixInput = (value) => {
      // 轉換為大寫
      const upperValue = value.toUpperCase()
      prefix.value = upperValue
      emitValue()
    }

    // 處理後綴輸入
    const handleSuffixInput = (value) => {
      // 轉換為大寫
      const upperValue = value.toUpperCase()
      suffix.value = upperValue
      emitValue()
    }

    // 發送完整車牌值
    const emitValue = () => {
      const fullPlate = prefix.value && suffix.value
        ? `${prefix.value}-${suffix.value}`
        : ''
      emit('update:value', fullPlate)
    }

    // 處理 blur 事件
    const handleBlur = () => {
      emit('blur')
    }

    initializeValue()

    return {
      prefix,
      suffix,
      handlePrefixInput,
      handleSuffixInput,
      handleBlur,
    }
  },
})
</script>

<style lang="postcss" scoped>
.license-plate-input-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dash-separator {
  font-size: 18px;
  font-weight: bold;
  color: #666;
  user-select: none;
}
</style>
